<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import AudienceService from '@/services/AudienceService'
import { useOptimisticActions } from '@/composables/useOptimisticActions'
import { MOBILE_SHEET_WIDTH } from './audienceDialog'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconBin from '@/components/icon/IconBin.vue'

const { notify } = useNotification()

// The room's own limits, restated so a host meets them in the form instead of
// meeting them as a rejection or a silent clamp. See castr-audience: options
// are 2..6, a question is 300 characters, an option 120, and durationSec is
// capped at 86400.
const MIN_OPTIONS = 2
const MAX_OPTIONS = 6
const QUESTION_MAX = 300
const OPTION_MAX = 120
const MAX_MINUTES = 1440

const props = defineProps({
	streamId: {
		type: String,
		required: true
	},
	polls: {
		type: Array,
		default: () => []
	},
	connected: {
		type: Boolean,
		default: false
	}
})

const createModalVisible = ref(false)
const creating = ref(false)

const question = ref('')
const options = ref(['', ''])
const durationMinutes = ref('')

const questionRow = ref(null)
const optionRows = ref([])

// Closing a poll is the same proxied round trip as the chat actions, waiting
// on a poll.close broadcast. See useOptimisticActions.
const moderation = useOptimisticActions()

const closeKey = (id) => `poll.close:${id}`

/** Polls with a close the moderator has asked for already reflected. */
const visiblePolls = computed(() => props.polls
	.map((poll) => (moderation.has(closeKey(poll.id)) && !poll.closedTs
		? { ...poll, closedTs: moderation.get(closeKey(poll.id)) }
		: poll)))

const openCount = computed(() => visiblePolls.value.filter((p) => !p.closedTs).length)

watch(() => props.polls, () => {
	const closed = new Set(props.polls.filter((p) => p.closedTs).map((p) => p.id))
	moderation.settle((key) => closed.has(Number(key.slice(closeKey('').length))))
}, { deep: true })

/* ------------------------------ the form ------------------------------- */

const filledOptions = computed(() => options.value.map((o) => o.trim()).filter(Boolean))

const duplicateIndexes = computed(() => {
	const seen = new Map()
	const dupes = new Set()

	options.value.forEach((option, index) => {
		const key = option.trim().toLowerCase()
		if (!key) return
		if (seen.has(key)) {
			dupes.add(seen.get(key))
			dupes.add(index)
		} else {
			seen.set(key, index)
		}
	})

	return dupes
})

const minutesValue = computed(() => Number(durationMinutes.value))

const minutesInvalid = computed(() => durationMinutes.value !== ''
	&& (!Number.isFinite(minutesValue.value) || minutesValue.value < 1 || minutesValue.value > MAX_MINUTES))

/**
 * Why the button is off, in the order a host would fix them. A disabled
 * control that never says what it wants is the same dead end as a button that
 * does nothing.
 */
const problem = computed(() => {
	if (!question.value.trim()) return 'Add a question.'
	if (filledOptions.value.length < MIN_OPTIONS) return `Fill in at least ${MIN_OPTIONS} options.`
	if (duplicateIndexes.value.size) return 'Two options are the same. Viewers cannot tell them apart.'
	if (minutesInvalid.value) return `Auto-close must be between 1 and ${MAX_MINUTES} minutes.`
	return ''
})

const canSubmit = computed(() => !problem.value)

const openCreate = async () => {
	question.value = ''
	options.value = ['', '']
	durationMinutes.value = ''
	createModalVisible.value = true

	// Straight into the first field: opening a poll mid-broadcast is a race,
	// and the click into Question was pure overhead.
	await nextTick()
	focusIn(questionRow.value)
}

const focusIn = (row) => {
	const input = row && row.querySelector('input')
	if (input) input.focus()
}

const focusOption = async (index) => {
	await nextTick()
	focusIn(optionRows.value[index])
}

const addOption = async (focus = true) => {
	if (options.value.length >= MAX_OPTIONS) return

	options.value.push('')
	if (focus) await focusOption(options.value.length - 1)
}

const removeOption = (index) => {
	if (options.value.length <= MIN_OPTIONS) return
	options.value.splice(index, 1)
	optionRows.value.splice(index, 1)
}

/**
 * Enter walks the list the way a host fills it in: on to the next option, and
 * off the end of it into a new one. Only on the last option of a full list
 * does it mean "that is the poll, open it".
 */
const onOptionEnter = (index) => {
	if (index < options.value.length - 1) return focusOption(index + 1)
	if (options.value.length < MAX_OPTIONS) return addOption()
	if (canSubmit.value) return createPoll()
	return undefined
}

const createPoll = async () => {
	if (!canSubmit.value || creating.value) return

	creating.value = true
	try {
		const payload = {
			question: question.value.trim(),
			options: filledOptions.value
		}

		if (Number.isFinite(minutesValue.value) && minutesValue.value > 0) {
			payload.durationSec = Math.min(Math.round(minutesValue.value * 60), MAX_MINUTES * 60)
		}

		await AudienceService.openPoll(props.streamId, payload)
		notify({ group: 'success', text: 'Poll opened' })
		createModalVisible.value = false
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not open the poll' })
	}
	creating.value = false
}

const closePoll = (poll) => moderation.run({
	key: closeKey(poll.id),
	effect: Date.now(),
	request: () => AudienceService.closePoll(props.streamId, poll.id),
	failure: 'The poll is still open — closing it failed.'
})

const totalVotes = (poll) => (poll.counts || []).reduce((sum, n) => sum + n, 0)

const share = (poll, index) => {
	const total = totalVotes(poll)
	if (!total) return 0
	return Math.round(((poll.counts[index] || 0) / total) * 100)
}

/** The option in front, so a glance at a live poll lands on the answer. */
const leaderIndex = (poll) => {
	if (!totalVotes(poll)) return -1

	let best = -1
	let bestCount = -1
	let tied = false

	;(poll.counts || []).forEach((count, index) => {
		if (count > bestCount) {
			bestCount = count
			best = index
			tied = false
		} else if (count === bestCount) {
			tied = true
		}
	})

	return tied ? -1 : best
}
</script>

<template>
	<div class="flex flex-col h-full">
		<div v-if="visiblePolls.length" class="flex items-center justify-between gap-x-3 shrink-0 mb-2">
			<p class="text-tiny text-surface-7 mb-0">
				{{ openCount }} live<template v-if="visiblePolls.length - openCount">,
					{{ visiblePolls.length - openCount }} closed</template>
			</p>
			<FormButton type="main" size="sm" :disabled="!connected" @click="openCreate">
				<IconBase class="!mr-1"><IconPlus /></IconBase>
				New poll
			</FormButton>
		</div>

		<div class="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
			<!-- With nothing running, the pane's whole job is to get one
			started, so the call to action is the pane rather than a ghost
			button in the corner above four hundred pixels of nothing. -->
			<div v-if="!visiblePolls.length" class="flex flex-col items-center text-center px-6 py-10">
				<span class="flex items-center justify-center w-11 h-11 rounded-full bg-surface-3 mb-3">
					<IconBase class="!w-5 !h-5 text-main"><IconPlus /></IconBase>
				</span>
				<p class="text-base font-medium text-surface-text-1 mb-1">No polls yet</p>
				<p class="text-sm text-surface-7 max-w-sm mb-4">
					Ask the room something and watch the answers arrive live. Viewers vote in one tap, and
					everyone sees the result update as it happens.
				</p>
				<FormButton type="main" :disabled="!connected" @click="openCreate">
					<IconBase class="!mr-1"><IconPlus /></IconBase>
					New poll
				</FormButton>
				<p v-if="!connected" class="text-tiny text-surface-7 mt-2 mb-0">
					Connect to the room to run polls.
				</p>
			</div>

			<div v-for="poll in visiblePolls" :key="poll.id" class="rounded-lg p-3 mb-2 border"
				:class="poll.closedTs
					? 'bg-surface-2/50 border-transparent'
					: 'bg-surface-2 border-main/40'">
				<div class="flex items-start justify-between gap-x-3 mb-2">
					<div class="min-w-0">
						<p class="flex items-center gap-x-1.5 text-tiny leading-none mb-1"
							:class="poll.closedTs ? 'text-surface-7' : 'text-success'">
							<span class="w-1.5 h-1.5 rounded-full"
								:class="poll.closedTs ? 'bg-surface-6' : 'bg-success'"></span>
							{{ poll.closedTs ? 'Closed' : 'Live' }}
							<span class="text-surface-7">· {{ totalVotes(poll) }} votes</span>
						</p>
						<p class="text-sm font-medium text-surface-text-1 break-words mb-0" dir="auto">
							{{ poll.question }}
						</p>
					</div>

					<FormButton v-if="!poll.closedTs" type="secondary" size="sm" class="shrink-0"
						@click="closePoll(poll)">
						Close
					</FormButton>
				</div>

				<div v-for="(option, index) in poll.options" :key="index"
					class="relative overflow-hidden bg-surface-3 rounded-md mb-1">
					<div class="absolute inset-y-0 left-0 transition-all duration-300"
						:class="poll.closedTs ? 'bg-surface-6/40' : 'bg-main/30'"
						:style="{ width: share(poll, index) + '%' }"></div>
					<div class="relative flex items-center justify-between px-3 py-1.5">
						<span class="text-sm break-words" dir="auto"
							:class="index === leaderIndex(poll)
								? 'text-surface-text-1 font-medium'
								: 'text-surface-text-1'">
							{{ option }}
						</span>
						<span class="text-tiny tabular-nums whitespace-nowrap ml-3"
							:class="index === leaderIndex(poll) ? 'text-surface-text-1' : 'text-surface-7'">
							{{ (poll.counts && poll.counts[index]) || 0 }} · {{ share(poll, index) }}%
						</span>
					</div>
				</div>
			</div>
		</div>

		<!-- scroll-body because a poll at six options is taller than the md
		card's fixed 480px. The body carries its own padding, as every other
		dialog in this tab does: the slot sits flush inside a frame whose
		scrollbar gutter would otherwise eat the last few characters of any
		line that reaches the right edge. -->
		<ModalBox v-model="createModalVisible" title="New poll" size="md" scroll-body
			:wrapper-classes="MOBILE_SHEET_WIDTH">
			<div class="flex flex-col gap-y-3 px-4 pb-4 sm:px-6 sm:pb-6"
				@keydown.esc="createModalVisible = false">
				<div ref="questionRow">
					<FormInput v-model="question" label="Question" placeholder="Which topic next?"
						:maxlength="QUESTION_MAX" />
					<!-- Only once it is information. A counter sitting at 0 / 300
					is noise in a dialog already short of room. -->
					<p v-if="question.length > QUESTION_MAX - 100"
						class="text-tiny text-surface-7 text-right mt-1 mb-0">
						{{ question.length }} / {{ QUESTION_MAX }}
					</p>
				</div>

				<div>
					<div class="flex items-baseline justify-between gap-x-2 mb-2">
						<p class="text-sm text-surface-text-1 mb-0">Options</p>
						<p class="text-tiny text-surface-7 whitespace-nowrap mb-0">
							{{ options.length }} of {{ MAX_OPTIONS }}
						</p>
					</div>

					<div v-for="(option, index) in options" :key="index"
						:ref="(el) => { if (el) optionRows[index] = el }"
						class="flex items-center gap-x-2 mb-2" @keydown.enter.prevent="onOptionEnter(index)">
						<FormInput v-model="options[index]" :placeholder="`Option ${index + 1}`"
							:maxlength="OPTION_MAX" :is-error="duplicateIndexes.has(index)"
							custom-classes="flex-1" />
						<!-- Rendered only when it can do something. A disabled
						icon square teaches a host that buttons here are dead. -->
						<FormButton v-if="options.length > MIN_OPTIONS" type="light" size="sm" is-icon
							:title="`Remove option ${index + 1}`" @click="removeOption(index)">
							<IconBase><IconBin /></IconBase>
						</FormButton>
						<span v-else class="w-8 shrink-0"></span>
					</div>

					<!-- Adding an option is the common action in this section,
					so it carries at least as much weight as the rows it makes. -->
					<div class="flex flex-wrap items-center gap-x-2 gap-y-1">
						<FormButton type="secondary" size="sm" :disabled="options.length >= MAX_OPTIONS"
							@click="addOption()">
							<IconBase class="!mr-1"><IconPlus /></IconBase>
							Add option
						</FormButton>
						<span v-if="options.length >= MAX_OPTIONS" class="text-tiny text-surface-7">
							Six is the most the room accepts.
						</span>
					</div>
				</div>

				<FormInput v-model="durationMinutes" type="number" label="Auto-close after (minutes)"
					placeholder="Optional" :is-error="minutesInvalid"
					:helper="`Up to 24 hours. Leave it empty to close the poll yourself.`" />
			</div>

			<!-- The footer says one thing: either why the poll cannot be opened
			yet, or — once it can — what opening it commits to. Both belong
			beside the button that acts on them, and only one is ever true, so
			they cost a single line rather than a paragraph in the body. -->
			<template #footer>
				<footer
					class="flex flex-col gap-3 border-t border-surface-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
					<p class="text-tiny mb-0 min-w-0" :class="problem ? 'text-warning' : 'text-surface-7'">
						{{ problem || 'A poll cannot be edited once it is open.' }}
					</p>
					<div class="flex gap-x-2 shrink-0 max-sm:justify-end">
						<FormButton type="secondary" @click="createModalVisible = false">Cancel</FormButton>
						<FormButton :disabled="!canSubmit" :is-loading="creating" @click="createPoll">
							Open poll
						</FormButton>
					</div>
				</footer>
			</template>
		</ModalBox>
	</div>
</template>
